import type { RealtimeChannel } from '@supabase/supabase-js';
import { supabase } from '../utils/supabaseClient';
import type { AppNotification } from '../types/Notification';

export type RealtimeConnectionState =
  | 'connecting'
  | 'connected'
  | 'disconnected';

interface NotificationSubscriptionHandlers {
  onInsert: (notification: AppNotification) => void;
  onUpdate: (notification: AppNotification) => void;
  onDelete: (notificationId: number) => void;
  onConnectionChange: (state: RealtimeConnectionState) => void;
  /**
   * Fires after the channel (re)joins. Any notification written while the
   * socket was down was never delivered, so the caller must re-fetch here —
   * without it the bell silently misses every call taken during a dropped
   * connection.
   */
  onResync: () => void;
}

/** Exponential backoff, capped. Matches the socket's own retry feel. */
const RETRY_DELAYS_MS = [1_000, 2_000, 5_000, 10_000, 30_000];

/**
 * Subscribes to the signed-in user's notification feed.
 *
 * Filtering on `user_id` server-side keeps the socket quiet: the database sends
 * this client only its own rows, rather than every row and letting RLS reject
 * the rest.
 *
 * @returns An unsubscribe function. Safe to call more than once.
 */
export function subscribeToNotifications(
  userId: string,
  handlers: NotificationSubscriptionHandlers
): () => void {
  let channel: RealtimeChannel | null = null;
  let retryIndex = 0;
  let retryTimer: ReturnType<typeof setTimeout> | undefined;
  let disposed = false;
  // The first SUBSCRIBED is the initial load, not a recovery; only resync on
  // the ones after it.
  let hasJoinedBefore = false;

  const clearRetry = () => {
    if (retryTimer !== undefined) {
      clearTimeout(retryTimer);
      retryTimer = undefined;
    }
  };

  const scheduleRetry = () => {
    if (disposed || retryTimer !== undefined) return;
    const delay =
      RETRY_DELAYS_MS[Math.min(retryIndex, RETRY_DELAYS_MS.length - 1)];
    retryIndex += 1;
    retryTimer = setTimeout(() => {
      retryTimer = undefined;
      if (disposed) return;
      void teardown().then(connect);
    }, delay);
  };

  const teardown = async () => {
    if (!channel) return;
    const stale = channel;
    channel = null;
    try {
      await supabase.removeChannel(stale);
    } catch (error) {
      console.warn('Failed to remove notifications channel', error);
    }
  };

  const connect = async () => {
    if (disposed) return;
    handlers.onConnectionChange('connecting');

    // Realtime evaluates user_notifications' RLS policy as the subscriber, and
    // the claims it uses are baked into the channel's JOIN payload. supabase-js
    // sets the socket's token from its own auth listener, which races this
    // subscribe on first load: if the join wins, the subscription is registered
    // with the anon key, auth.uid() is null, the policy matches no row, and the
    // channel reports SUBSCRIBED while delivering nothing at all. Forcing a
    // token refresh first makes the ordering explicit.
    try {
      await supabase.realtime.setAuth();
    } catch (error) {
      console.warn('Could not refresh the realtime access token', error);
    }
    if (disposed) return;

    // Unique per attempt, never just the user id. RealtimeClient.channel()
    // returns an *existing* channel when the topic matches, and removeChannel()
    // is async — so under StrictMode's deliberate mount/unmount/mount, the
    // second mount would be handed back the very channel the first mount was
    // still tearing down. Bindings added after a join are ignored, and the
    // pending removal then kills the subscription: a channel that reports
    // healthy and delivers nothing.
    const topic = `user-notifications:${userId}:${Math.random().toString(36).slice(2)}`;

    channel = supabase
      .channel(topic)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'user_notifications',
          filter: `user_id=eq.${userId}`,
        },
        payload => handlers.onInsert(payload.new as AppNotification)
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'user_notifications',
          filter: `user_id=eq.${userId}`,
        },
        payload => handlers.onUpdate(payload.new as AppNotification)
      )
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'user_notifications',
          // No filter: a DELETE payload carries only the primary key, so
          // there is no user_id on it to match against.
          //
          // Replica identity is DEFAULT, so `old` holds the id and nothing
          // more. Reconciling against local state is enough — an id we do not
          // hold is simply ignored.
        },
        payload => {
          const id = Number((payload.old as { id?: number })?.id);
          if (Number.isFinite(id)) handlers.onDelete(id);
        }
      )
      .subscribe((status, error) => {
        if (disposed) return;

        if (status === 'SUBSCRIBED') {
          clearRetry();
          retryIndex = 0;
          handlers.onConnectionChange('connected');
          if (hasJoinedBefore) handlers.onResync();
          hasJoinedBefore = true;
          return;
        }

        if (
          status === 'CHANNEL_ERROR' ||
          status === 'TIMED_OUT' ||
          status === 'CLOSED'
        ) {
          // A silently dead channel is the worst failure mode here: the bell
          // looks fine and simply never updates. Say so.
          console.warn(
            `Notifications channel ${status.toLowerCase()}; retrying.`,
            error ?? ''
          );
          handlers.onConnectionChange('disconnected');
          scheduleRetry();
        }
      });
  };

  void connect();

  return () => {
    disposed = true;
    clearRetry();
    void teardown();
  };
}
